import { supabase } from '../../lib/supabase';

// Define the type for the lease lots data
export interface LeaseLot {
  leaselot_lease: number;
  leaselot_lot: string;
  leaselot_use: string | null;
  // Include additional lot details from join
  lot_perm_use?: string | null;
  lot_community?: string | null;
  lot_desc?: string | null;
  community_name?: string | null;
  lot_id?: string | null;
}

// Error reason type
export type ErrorReason = 'sublease-not-found' | 'no-lots-found' | 'query-error' | 'unexpected-error' | 'not-authenticated' | '';

export const supabaseLeaseLotService = {
  // Get lots associated with a specific sublease
  getByLeaseId: async (leaseId: number): Promise<{ lots: LeaseLot[], error: boolean, errorReason: ErrorReason, errorMessage: string }> => {
    console.log(`Fetching lots for sublease ID ${leaseId}...`);
    
    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.log('No authenticated session');
      return {
        lots: [],
        error: true,
        errorReason: 'not-authenticated',
        errorMessage: 'User is not authenticated. Please log in to view data.'
      };
    }
    
    try {
      // First, verify the sublease exists in the database
      const { data: subleaseExists, error: subleaseError } = await supabase
        .from('subleases')
        .select('lease_id, lease_holder, lease_start, lease_end')
        .eq('lease_id', leaseId)
        .single();
      
      if (subleaseError || !subleaseExists) {
        console.warn(`Sublease ID ${leaseId} not found in database`);
        return { 
          lots: [],
          error: true,
          errorReason: "sublease-not-found",
          errorMessage: `Sublease ID ${leaseId} could not be found in the database.`
        };
      }
      
      console.log(`Sublease found with ID ${leaseId}, start date ${subleaseExists.lease_start}, end date ${subleaseExists.lease_end}`);
      console.log('Executing Supabase query on lease_lots table...');
      
      // Query lease_lots with joins, handling them separately to avoid schema cache issues
      const { data: lotLeaseData, error: lotLeaseError } = await supabase
        .from('lease_lots')
        .select(`
          leaselot_lease,
          leaselot_lot,
          leaselot_use,
          land_lots:leaselot_lot (
            lot_id,
            lot_perm_use,
            lot_community,
            lot_desc
          )
        `)
        .eq('leaselot_lease', leaseId)
        .order('leaselot_lot', { ascending: true });
      
      console.log('Supabase lease_lots query executed');
      
      if (lotLeaseError) {
        console.error(`Error fetching lots for sublease ID ${leaseId}:`, lotLeaseError);
        return {
          lots: [],
          error: true,
          errorReason: "query-error",
          errorMessage: `Error querying the database: ${lotLeaseError.message}`
        };
      }
      
      if (!lotLeaseData || lotLeaseData.length === 0) {
        console.log(`No lease_lots records found for sublease ID ${leaseId}, checking for valid lots directly...`);
        
        // If no lease_lots records, try to query available lots that might be related
        // This is a fallback to handle missing lease_lots entries
        const { data: availableLots, error: availableLotsError } = await supabase
          .from('land_lots')
          .select(`
            lot_id,
            lot_perm_use,
            lot_community,
            lot_desc,
            communities (
              comm_name
            )
          `)
          .order('lot_id', { ascending: true })
          .limit(5); // Limit to 5 lots as a sample
        
        if (availableLotsError || !availableLots || availableLots.length === 0) {
          return {
            lots: [],
            error: true,
            errorReason: "no-lots-found",
            errorMessage: `No lots are associated with sublease ID ${leaseId} in the database. This sublease exists but has no lots linked to it.`
          };
        }
        
        // Return a helpful message
        return {
          lots: [],
          error: true,
          errorReason: "no-lots-found",
          errorMessage: `Sublease ID ${leaseId} exists but has no lots associated in the lease_lots table. The database has ${availableLots.length} lots available that could be linked to this sublease.`
        };
      }
      
      console.log(`Found ${lotLeaseData.length} lots for sublease ID ${leaseId}`);
      
      // First extract just the community IDs from the land_lots
      const communityIds = lotLeaseData
        .map(leaseLot => {
          const landLot = leaseLot.land_lots as any;
          return landLot?.lot_community;
        })
        .filter(id => id !== null && id !== undefined);
      
      // If we have any community IDs, fetch the community names
      let communityMap: Record<string, string> = {};
      
      if (communityIds.length > 0) {
        try {
          const { data: communities, error: commError } = await supabase
            .from('communities')
            .select('comm_id, comm_name')
            .in('comm_id', communityIds);
            
          if (!commError && communities) {
            // Create a mapping of community IDs to names
            communityMap = communities.reduce((map, comm) => {
              map[comm.comm_id] = comm.comm_name;
              return map;
            }, {} as Record<string, string>);
            
            console.log(`Fetched ${communities.length} communities for the lots`);
          } else {
            console.warn('Could not fetch community names:', commError);
          }
        } catch (commFetchError) {
          console.error('Error fetching communities:', commFetchError);
        }
      }
      
      // Transform the data to include lot details directly
      const leaseLots: LeaseLot[] = lotLeaseData.map(leaseLot => {
        // Extract land_lots data
        const landLot = leaseLot.land_lots as any;
        const communityId = landLot?.lot_community;
        
        return {
          leaselot_lease: leaseLot.leaselot_lease,
          leaselot_lot: leaseLot.leaselot_lot,
          leaselot_use: leaseLot.leaselot_use,
          lot_id: landLot?.lot_id || leaseLot.leaselot_lot, // Use leaselot_lot as fallback
          lot_perm_use: landLot?.lot_perm_use || null,
          lot_community: communityId || null,
          lot_desc: landLot?.lot_desc || null,
          community_name: communityId ? communityMap[communityId] || `Community ${communityId}` : null
        };
      });
      
      // Validate that all lots belong to this sublease (should always be true due to the query)
      const invalidLots = leaseLots.filter(lot => lot.leaselot_lease !== leaseId);
      if (invalidLots.length > 0) {
        console.warn(`Found ${invalidLots.length} lots with incorrect lease ID reference`);
      }
      
      return {
        lots: leaseLots,
        error: false,
        errorReason: "",
        errorMessage: ""
      };
    } catch (error) {
      console.error(`Unexpected error in getByLeaseId for ID ${leaseId}:`, error);
      return {
        lots: [],
        error: true,
        errorReason: "unexpected-error",
        errorMessage: `An unexpected error occurred while fetching data: ${error instanceof Error ? error.message : String(error)}`
      };
    }
  },

  // Get lots for multiple lease IDs
  async getLotsForMultipleLeaseIds(leaseIds: number[]): Promise<Record<number, LeaseLot[]>> {
    if (!leaseIds || leaseIds.length === 0) {
      console.log('getLotsForMultipleLeaseIds: No lease IDs provided.');
      return {};
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.warn('getLotsForMultipleLeaseIds: No authenticated session.');
      return {};
    }

    console.log(`Fetching lots for ${leaseIds.length} lease IDs:`, leaseIds);

    try {
      const { data: lotLeaseData, error: lotLeaseError } = await supabase
        .from('lease_lots')
        .select(`
          leaselot_lease,
          leaselot_lot,
          leaselot_use,
          land_lots:leaselot_lot (
            lot_id,
            lot_perm_use,
            lot_community,
            lot_desc
          )
        `) // Corrected template literal
        .in('leaselot_lease', leaseIds)
        .order('leaselot_lot', { ascending: true });

      if (lotLeaseError) {
        console.error('Error fetching lots for multiple lease IDs:', lotLeaseError.message);
        return {};
      }

      if (!lotLeaseData || lotLeaseData.length === 0) {
        console.log('No lease_lots records found for the provided lease IDs.');
        return {};
      }

      console.log(`Found ${lotLeaseData.length} total lease_lot entries for the provided lease IDs.`);

      const communityIds = lotLeaseData
        .map(ll => (ll.land_lots as any)?.lot_community)
        .filter(id => id != null);
      
      let communityMap: Record<string, string> = {};
      if (communityIds.length > 0) {
        const uniqueCommunityIds = [...new Set(communityIds)];
        console.log(`Fetching ${uniqueCommunityIds.length} unique community names...`);
        const { data: communities, error: commError } = await supabase
          .from('communities')
          .select('comm_id, comm_name')
          .in('comm_id', uniqueCommunityIds);
          
        if (commError) {
          console.warn('Could not fetch community names:', commError.message);
        } else if (communities) {
          communityMap = communities.reduce((map, comm) => {
            map[comm.comm_id] = comm.comm_name;
            return map;
          }, {} as Record<string, string>);
          console.log(`Fetched ${Object.keys(communityMap).length} community names successfully.`);
        }
      }

      const groupedLots: Record<number, LeaseLot[]> = {};
      for (const leaseLotItem of lotLeaseData) {
        const landLotData = leaseLotItem.land_lots as any; // Use a different variable name to avoid conflict
        const communityId = landLotData?.lot_community;
        
        const transformedLot: LeaseLot = {
          leaselot_lease: leaseLotItem.leaselot_lease,
          leaselot_lot: leaseLotItem.leaselot_lot,
          leaselot_use: leaseLotItem.leaselot_use,
          lot_id: landLotData?.lot_id || leaseLotItem.leaselot_lot,
          lot_perm_use: landLotData?.lot_perm_use || null,
          lot_community: communityId || null,
          lot_desc: landLotData?.lot_desc || null,
          community_name: communityId && communityMap[communityId] ? communityMap[communityId] : null,
        };

        if (!groupedLots[transformedLot.leaselot_lease]) {
          groupedLots[transformedLot.leaselot_lease] = [];
        }
        groupedLots[transformedLot.leaselot_lease].push(transformedLot);
      }
      
      console.log(`Grouped lots for ${Object.keys(groupedLots).length} lease IDs.`);
      return groupedLots;

    } catch (error) {
      console.error('Unexpected error in getLotsForMultipleLeaseIds:', error instanceof Error ? error.message : String(error));
      return {}; // Ensure all paths return a value or throw
    }
  }
};
